export const PAN_REGEX = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/;

// Pulls state/city/pincode out of a Nominatim `address` object (from a reverse-geocode
// or a search result fetched with addressdetails=1). City can land under several keys
// depending on how OSM tagged the area, so fall back through the common ones.
export const extractLocationDetails = (address) => {
  if (!address) return null;
  const city =
    address.city ||
    address.city_district ||
    address.town ||
    address.village ||
    address.municipality ||
    address.county ||
    address.state_district ||
    "";
  return {
    state: address.state || "",
    city,
    pincode: address.postcode || "",
  };
};

// A handful of Indian cities OSM still reports under their old name, while internal
// master lists use the official renamed one (or vice versa) — neither is a substring
// of the other, so a plain fuzzy match misses them.
const CITY_ALIASES = {
  gurgaon: "gurugram",
  bombay: "mumbai",
  bangalore: "bengaluru",
  calcutta: "kolkata",
  madras: "chennai",
  trivandrum: "thiruvananthapuram",
  cochin: "kochi",
  poona: "pune",
  baroda: "vadodara",
  mysore: "mysuru",
  allahabad: "prayagraj",
};

// The city master list's naming doesn't always match OSM's spelling/casing exactly
// (trailing qualifiers, spacing, or an old-vs-official name), so try an exact match,
// then a substring match either way, then known aliases before giving up.
export const matchCityName = (list, name) => {
  if (!name) return null;
  const norm = (s) => String(s).trim().toLowerCase();
  const target = norm(name);
  const alias = CITY_ALIASES[target];
  const reverseAlias = Object.entries(CITY_ALIASES).find(([, v]) => v === target)?.[0];

  return (
    list.find((c) => norm(c) === target) ||
    list.find((c) => norm(c).includes(target) || target.includes(norm(c))) ||
    (alias && list.find((c) => norm(c) === alias)) ||
    (reverseAlias && list.find((c) => norm(c) === reverseAlias)) ||
    null
  );
};

export const fileNameFromPath = (path) => {
  if (!path) return "";
  const parts = String(path).split("/");
  return parts[parts.length - 1] || String(path);
};

export const buildLogoUrl = (value, apiBase) => {
  if (!value) return "";
  if (/^https?:\/\//i.test(value)) return value;
  const baseUrl = apiBase || process.env.REACT_APP_API_URL || "";
  const sep = baseUrl && baseUrl.endsWith("/") ? "" : "/";
  const fileParam = encodeURIComponent(value);
  return `${baseUrl}${sep}patient-medical-history/file-url?fileName=${fileParam}`;
};

export const extractSignedUrlFromResponse = (data) => {
  if (!data) return "";
  if (typeof data === "string" && /^https?:\/\//i.test(data)) return data;
  if (typeof data.successObject === "string" && /^https?:\/\//i.test(data.successObject)) {
    return data.successObject;
  }
  if (typeof data.signedUrl === "string" && /^https?:\/\//i.test(data.signedUrl)) {
    return data.signedUrl;
  }
  if (typeof data.url === "string" && /^https?:\/\//i.test(data.url)) {
    return data.url;
  }
  return "";
};

export const loadLogoPreviewWithAuthToSetter = async (fileName, setPreview, apiBase) => {
  try {
    const token = localStorage.getItem("auth_token");
    const tenantId = localStorage.getItem("tenantId");
    if (!fileName || !token || !tenantId) return;

    if (/^https?:\/\//i.test(fileName)) {
      setPreview(fileName);
      return;
    }

    const url = buildLogoUrl(fileName, apiBase);
    const res = await fetch(url, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`,
        tenantId: tenantId,
        Accept: "*/*",
      },
      redirect: "follow",
    });
    if (!res.ok) return;
    const contentType = res.headers.get("content-type") || "";
    if (contentType.includes("application/json")) {
      try {
        const data = await res.json();
        const signedUrl = extractSignedUrlFromResponse(data);
        if (signedUrl) {
          setPreview(signedUrl);
          return;
        }
      } catch (_) {
        // fall through
      }
    } else if (contentType.includes("text/")) {
      const text = await res.text();
      if (/^https?:\/\//i.test(text.trim())) {
        setPreview(text.trim());
        return;
      }
    }
    const blob = await res.blob();
    if (blob.size > 0) {
      setPreview(URL.createObjectURL(blob));
    }
  } catch (e) {
    console.error("Failed to load file preview:", e);
  }
};

export const parseApiError = async (res) => {
  const text = await res.text();
  let message = `Failed (${res.status})`;
  try {
    const maybeJson = JSON.parse(text);
    message = maybeJson?.error || maybeJson?.message || text || message;
  } catch {
    message = text || message;
  }
  return message;
};
